import type { SchemaAttributes } from '@/api/types'
import type { MockServer } from '@/api/mock-server/server'
import { getCurrentTimestamp } from '@/utils/date/getter.js'
import type { ReportExportStatusResponse } from '@/composables/api/ReportExport.js'

// Store for tracking export jobs
const exportJobs: Map<
  string,
  {
    active: boolean
    succeeded: boolean
    createdAt: number
    statusCallCount: number
  }
> = new Map()

// TTL for export jobs (5 minutes)
const EXPORT_JOB_TTL_MS = 5 * 60 * 1000

/**
 * Clean up old export jobs to prevent memory leaks.
 * Removes jobs older than EXPORT_JOB_TTL_MS.
 */
function cleanupOldJobs() {
  const now = getCurrentTimestamp()
  for (const [taskId, job] of exportJobs) {
    if (now - job.createdAt > EXPORT_JOB_TTL_MS) {
      exportJobs.delete(taskId)
    }
  }
}

export function routes(server: MockServer) {
  // Step 1: POST - Start export job
  // Frontend expects: { data: { type: 'ExportTask', id: string, attributes: { task_id: string } } }
  server.post('/reports_v2/export/', function () {
    // Clean up old jobs before creating new ones
    cleanupOldJobs()

    const taskId = crypto.randomUUID()

    // Store the export job
    exportJobs.set(taskId, {
      active: true,
      succeeded: false,
      createdAt: getCurrentTimestamp(),
      statusCallCount: 0,
    })

    return {
      data: {
        id: taskId,
        type: 'ExportTask',
        attributes: {
          task_id: taskId,
        },
      },
    }
  })

  // Step 2: GET - Check export status
  // Real BE returns: { data: { type: 'StatusReport', id: null, attributes: { active, succeeded, condition } } }
  // Frontend uses query param: task_id
  // Simulates realistic progression through 6 status calls:
  // Calls 1-5: active=true, succeeded=false, condition=null
  // Call 6+: active=false, succeeded=true, condition=null
  server.get('/reports_v2/export/status/', function (_schema, request) {
    // Clean up old jobs on status check (called frequently during polling)
    cleanupOldJobs()

    const taskId = request.queryParams['task_id'] as string

    const job = exportJobs.get(taskId)

    if (!job) {
      return {
        data: {
          id: null,
          type: 'StatusReport',
          attributes: {
            active: false,
            succeeded: false,
            condition: {
              reason: 'Export job not found',
              message: 'Export job not found',
              type: 'NotFound',
            },
          },
        },
      } satisfies ReportExportStatusResponse
    }

    // Increment status call count
    job.statusCallCount += 1

    // Simulate realistic status progression based on call count
    // Calls 1-5: job is still running (active=true)
    // Call 6+: job is completed (active=false, succeeded=true)
    if (job.statusCallCount >= 1 && job.statusCallCount <= 5) {
      job.active = true
      job.succeeded = false
    } else if (job.statusCallCount >= 6) {
      job.active = false
      job.succeeded = true
    }

    return {
      data: {
        id: null,
        type: 'StatusReport',
        attributes: {
          active: job.active,
          succeeded: job.succeeded,
          condition: null, // condition is null when no error (both for in-progress and success)
        },
      },
    } satisfies ReportExportStatusResponse
  })

  // Step 3: GET - Get export result
  // Frontend expects: { data: Schemas['Base64Document'] }
  // Frontend uses query param: task_id
  server.get('/reports_v2/export/result/', function (_schema, request) {
    const taskId = request.queryParams['task_id'] as string

    // Clean up the job from memory
    exportJobs.delete(taskId)

    // This is PDF file content, an A4 Page with text "This is a PDF file generated by the mock server."
    const pdfFileContent =
      'JVBERi0xLjcKCjEgMCBvYmogICUgZW50cnkgcG9pbnQKPDwKICAvVHlwZSAvQ2F0YWxvZwogIC9QYWdlcyAyIDAgUgo+PgplbmRvYmoKCjIgMCBvYmoKPDwKICAvVHlwZSAvUGFnZXMKICAvTWVkaWFCb3ggWyAwIDAgNTk1IDg0MiBdCiAgL0NvdW50IDEKICAvS2lkcyBbIDMgMCBSIF0KPj4KZW5kb2JqCgozIDAgb2JqCjw8CiAgL1R5cGUgL1BhZ2UKICAvUGFyZW50IDIgMCBSCiAgL1Jlc291cmNlcyA8PAogICAgL0ZvbnQgPDwKICAgICAgL0YxIDQgMCBSIAogICAgPj4KICA+PgogIC9Db250ZW50cyA1IDAgUgo+PgplbmRvYmoKCjQgMCBvYmoKPDwKICAvVHlwZSAvRm9udAogIC9TdWJ0eXBlIC9UeXBlMQogIC9CYXNlRm9udCAvSGVsdmV0aWNhLUJvbGQKPj4KZW5kb2JqCgo1IDAgb2JqICAlIHBhZ2UgY29udGVudAo8PAogIC9MZW5ndGggNDQKPj4Kc3RyZWFtCkJUCjE1MCA0MjEgVEQKL0YxIDE0IFRmCihUaGlzIGlzIGEgUERGIGZpbGUgZ2VuZXJhdGVkIGJ5IHRoZSBtb2NrIHNlcnZlcikgVGoKRVQKZW5kc3RyZWFtCmVuZG9iagoKeHJlZgowIDYKMDAwMDAwMDAwMCA2NTUzNSBmIAowMDAwMDAwMDEwIDAwMDAwIG4gCjAwMDAwMDAwNzkgMDAwMDAgbiAKMDAwMDAwMDE3MyAwMDAwMCBuIAowMDAwMDAwMzAxIDAwMDAwIG4gCjAwMDAwMDAzODAgMDAwMDAgbiAKdHJhaWxlcgo8PAogIC9TaXplIDYKICAvUm9vdCAxIDAgUgo+PgpzdGFydHhyZWYKNTI4CiUlRU9G'

    // Determine content based on format (we could track this in the job, but for simplicity use PDF)
    return {
      data: {
        id: taskId,
        type: 'Base64Document',
        attributes: {
          content: pdfFileContent,
          filename: 'invoice-report.pdf',
          content_type: 'application/pdf',
        } satisfies SchemaAttributes<'Base64Document'>,
      },
    }
  })

  // Keep the old endpoint for backward compatibility
  server.get('/reports/export', function (_schema, request) {
    const formatParam = request.queryParams['format'] as string
    const filenameParam = request.queryParams['filename'] as string
    const fullFileName = `${filenameParam}.${formatParam}`

    // This is PDF file content, an A4 Page with text "This is a PDF file generated by the mock server."
    const pdfFileContent =
      'JVBERi0xLjcKCjEgMCBvYmogICUgZW50cnkgcG9pbnQKPDwKICAvVHlwZSAvQ2F0YWxvZwogIC9QYWdlcyAyIDAgUgo+PgplbmRvYmoKCjIgMCBvYmoKPDwKICAvVHlwZSAvUGFnZXMKICAvTWVkaWFCb3ggWyAwIDAgNTk1IDg0MiBdCiAgL0NvdW50IDEKICAvS2lkcyBbIDMgMCBSIF0KPj4KZW5kb2JqCgozIDAgb2JqCjw8CiAgL1R5cGUgL1BhZ2UKICAvUGFyZW50IDIgMCBSCiAgL1Jlc291cmNlcyA8PAogICAgL0ZvbnQgPDwKICAgICAgL0YxIDQgMCBSIAogICAgPj4KICA+PgogIC9Db250ZW50cyA1IDAgUgo+PgplbmRvYmoKCjQgMCBvYmoKPDwKICAvVHlwZSAvRm9udAogIC9TdWJ0eXBlIC9UeXBlMQogIC9CYXNlRm9udCAvSGVsdmV0aWNhLUJvbGQKPj4KZW5kb2JqCgo1IDAgb2JqICAlIHBhZ2UgY29udGVudAo8PAogIC9MZW5ndGggNDQKPj4Kc3RyZWFtCkJUCjE1MCA0MjEgVEQKL0YxIDE0IFRmCihUaGlzIGlzIGEgUERGIGZpbGUgZ2VuZXJhdGVkIGJ5IHRoZSBtb2NrIHNlcnZlcikgVGoKRVQKZW5kc3RyZWFtCmVuZG9iagoKeHJlZgowIDYKMDAwMDAwMDAwMCA2NTUzNSBmIAowMDAwMDAwMDEwIDAwMDAwIG4gCjAwMDAwMDAwNzkgMDAwMDAgbiAKMDAwMDAwMDE3MyAwMDAwMCBuIAowMDAwMDAwMzAxIDAwMDAwIG4gCjAwMDAwMDAzODAgMDAwMDAgbiAKdHJhaWxlcgo8PAogIC9TaXplIDYKICAvUm9vdCAxIDAgUgo+PgpzdGFydHhyZWYKNTI4CiUlRU9G'

    return {
      data: {
        id: 'ReportExport',
        type: 'Base64Document',
        attributes: {
          content: pdfFileContent,
          filename: fullFileName,
          content_type: 'application/pdf',
        } satisfies SchemaAttributes<'Base64Document'>,
      },
    }
  })
}
